import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Modal, Box } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import axios from "axios";
import { CSSTransition } from "react-transition-group";

import { useSelector } from "react-redux";
import { navigationData } from "../jsonFiles/navigationData";

export default function Navbar({
  mainwidth,
  setMainWidth,
  setCanvasWidth,
  setOpacity,
  setHide,
  closeAllCollapses,
  isBelow1000px,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [employeeData, setEmployeeData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const navigate = useNavigate();

  const userId = localStorage.getItem("userId");
  const permissions = useSelector((state) => state.emp.permissions);

  // Prepare searchable navigation items based on permissions
  const searchableItems = React.useMemo(() => {
    const flattened = [];
    navigationData.forEach(item => {
      // Check top level first
      if (item.link && (item.id === "DashBoard" || permissions.includes(item.id))) {
        flattened.push({
          name: item.header,
          link: item.link,
          icon: item.icon,
          category: 'Main'
        });
      }
      // Check children
      if (item.Childs) {
        item.Childs.forEach(child => {
          if (permissions.includes(child.id)) {
            flattened.push({
              name: child.Name,
              link: child.link,
              icon: item.icon,
              category: item.header
            });
          }
        });
      }
    });
    return flattened;
  }, [permissions]);

  // Update search results
  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      const filtered = searchableItems.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filtered);
      setIsSearchOpen(true);
    } else {
      setSearchResults([]);
      setIsSearchOpen(false);
    }
  }, [searchQuery, searchableItems]);

  const handleSearchSelect = (link) => {
    navigate(link);
    setSearchQuery("");
    setIsSearchOpen(false);
  };

  // Handle click outside search
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.centerSide')) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch employee data
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const response = await axios.get(
          `${process.env.REACT_APP_API}/api/account/users-list/${userId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setEmployeeData(response.data);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    if (userId) {
      fetchEmployeeData();
    }
  }, [userId]);

  function settingWidth() {
    if (mainwidth === false) {
      setMainWidth(true);
      setCanvasWidth("250px");
      setTimeout(() => {
        setOpacity("100%");
        setHide("block");
      }, 300);
    } else {
      setMainWidth(false);
      setCanvasWidth("74px");
      setOpacity("0%");
      setHide("none");
      closeAllCollapses();
    }
  }

  const profileImageStyle = {
    backgroundImage: employeeData.profile_photo
      ? `url(${employeeData.profile_photo})`
      : `url(/assets/user.png)`,
    backgroundPosition: "center",
    backgroundSize: "cover",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    border: "1px solid white",
    cursor: "pointer",
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleLogout = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      const refreshToken = localStorage.getItem("refreshToken");

      await axios.post(
        `${process.env.REACT_APP_API}/api/account/logout/`,
        {
          refresh: refreshToken,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");

      navigate("/login");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    textAlign: "center",
    borderRadius: "13px",
  };

  // Attendance status state
  const [attendanceStatus, setAttendanceStatus] = useState(null);

  // Fetch attendance status
  useEffect(() => {
    const fetchAttendanceStatus = async () => {
      if (!userId) return;
      try {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        const punch_date = `${yyyy}-${mm}-${dd}`;
        const response = await axios.get(
          `${process.env.REACT_APP_API}/api/attendance/single-attendance/?punch_date=${punch_date}&employee__id=${userId}`
        );
        const data = response.data;
        if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0].punch_details) && data[0].punch_details.length > 0) {
          const lastPunch = data[0].punch_details[data[0].punch_details.length - 1];
          setAttendanceStatus(lastPunch.punch_direction); // "clock-in" or "clock-out"
        } else {
          setAttendanceStatus(null);
        }
      } catch (error) {
        setAttendanceStatus(null);
      }
    };
    fetchAttendanceStatus();
  }, [userId]);

  return (
    <div
      style={{
        backgroundColor: "var(--bg-color)",
        position: "sticky",
        top: "0",
        width: "100%",
        zIndex: "20",
        padding: "10px 0",
      }}
    >
      <nav className="navbar" style={{ padding: "0 24px" }}>
        <div className="Maincontainer-fluid w-100 d-flex align-items-center flex-nowrap justify-content-between">
          <div className="leftSide d-flex align-items-center justify-content-center">
            <button
              className="btn d-lg-none"
              type="button"
              data-bs-toggle={isBelow1000px ? "offcanvas" : ""}
              data-bs-target={isBelow1000px ? "#offcanvasExample" : ""}
              onClick={() => {
                if (!isBelow1000px) {
                  settingWidth();
                }
              }}
            >
              <i
                style={{ fontSize: "22px", color: "var(--text-main)" }}
                className="bi bi-list"
              ></i>
            </button>
          </div>
          <div className="centerSide w-50">
            <div className="d-flex w-100 position-relative">
              <div className="searchBox d-flex align-items-center w-75 position-relative" style={{ background: "white", borderRadius: "24px", padding: "4px 16px", boxShadow: "var(--shadow-sm)" }}>
                <span style={{ marginRight: "10px", color: "var(--text-muted)" }}>
                  <i className="bi bi-search"></i>
                </span>
                <input
                  className="form-control border-0"
                  type="search"
                  placeholder="Search navigation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery && setIsSearchOpen(true)}
                  aria-label="Search"
                  style={{ height: '36px', fontSize: '14px', background: "transparent", outline: "none", boxShadow: "none" }}
                />
                <span style={{ marginLeft: "auto", fontSize: "12px", background: "var(--bg-color)", padding: "2px 8px", borderRadius: "8px", color: "var(--text-muted)", fontWeight: "500" }}></span>
              </div>

              {/* Search Suggestions Dropdown */}
              {isSearchOpen && searchResults.length > 0 && (
                <div className="search-suggestions-dropdown shadow-lg" style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  width: '75%',
                  background: 'white',
                  borderRadius: '16px',
                  marginTop: '12px',
                  padding: '8px',
                  zIndex: 1000,
                  maxHeight: '400px',
                  overflowY: 'auto',
                  border: '1px solid rgba(0,0,0,0.05)'
                }}>
                  {searchResults.map((result, idx) => (
                    <div
                      key={idx}
                      className="search-result-item d-flex align-items-center p-3"
                      onClick={() => handleSearchSelect(result.link)}
                      style={{
                        cursor: 'pointer',
                        borderRadius: '10px',
                        transition: 'all 0.2s',
                        gap: '12px'
                      }}
                    >
                      <div className="search-icon-bg d-flex align-items-center justify-content-center" style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: 'var(--bg-color)',
                        color: 'var(--primary-color)'
                      }}>
                        <i className={result.icon || "bi bi-link-45deg"} style={{ fontSize: '18px' }}></i>
                      </div>
                      <div className="d-flex flex-column">
                        <span className="fw-bold text-dark" style={{ fontSize: '14px' }}>{result.name}</span>
                        <span className="text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{result.category}</span>
                      </div>
                      <i className="bi bi-arrow-right ms-auto text-muted small"></i>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="rightSide d-flex justify-content-evenly">
            {/* Attendance Status Indicator */}
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                marginRight: "1.5rem"
              }}
            >
              <span
                className={`status-dot ${attendanceStatus === "clock-in"
                  ? "active"
                  : attendanceStatus === "clock-out"
                    ? "inactive"
                    : ""
                  }`}
                style={{
                  display: "inline-block",
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  backgroundColor:
                    attendanceStatus === "clock-in"
                      ? "#00e676"
                      : attendanceStatus === "clock-out"
                        ? "#ff1744"
                        : "#bdbdbd",
                  boxShadow:
                    attendanceStatus === "clock-in"
                      ? "0 0 0 0.2rem rgba(0,230,118,0.5)"
                      : attendanceStatus === "clock-out"
                        ? "0 0 0 0.2rem rgba(255,23,68,0.5)"
                        : "none",
                  animation:
                    attendanceStatus === "clock-in" ||
                      attendanceStatus === "clock-out"
                      ? "blink 1s infinite alternate"
                      : "none"
                }}
                title={
                  attendanceStatus === "clock-in"
                    ? "Checked In"
                    : attendanceStatus === "clock-out"
                      ? "Checked Out"
                      : "No Attendance"
                }
              ></span>
              <style>
                {`
                  @keyframes blink {
                    0% { opacity: 1; }
                    100% { opacity: 0.4; }
                  }
                `}
              </style>
            </div>
            <div className="dropdown">
              <div
                className="profileImage"
                style={profileImageStyle}
                onClick={() => setDropdownOpen(!dropdownOpen)}
              ></div>
              {dropdownOpen && (
                <div
                  className="dropdown-menu show"
                  style={{
                    position: "absolute",
                    right: 35,
                    padding: "0 0 0.3rem 0",
                  }}
                >
                  <div className="user-info px-3 py-1 border-bottom">
                    <div
                      className="font-weight-bold fw-bolder"
                      style={{ fontSize: "clamp(11px, 1.2vw, 13px)" }}
                    >
                      {employeeData.name ? `${employeeData.name}` : "Admin"}
                    </div>
                    <div
                      className="text-muted small"
                      style={{ fontSize: "clamp(10px, 1vw, 11px)" }}
                    >
                      {employeeData.email}
                    </div>
                  </div>
                  <Link
                    to={`/user/empdetails/${userId}`}
                    className="dropdown-item"
                    style={{ fontSize: "clamp(11px, 1.2vw, 13px)" }}
                    onClick={() => setDropdownOpen(false)}
                  >
                    <i className="lni lni-user me-2"></i>Profile
                  </Link>
                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setOpenModal(true);
                      setDropdownOpen(false);
                    }}
                    style={{ fontSize: "clamp(11px, 1.2vw, 13px)" }}
                  >
                    <i className="lni lni-enter me-2"></i>Log out
                  </div>
                </div>
              )}
            </div>
          </div>
        </div >
      </nav >
      <CSSTransition
        in={openModal}
        timeout={300}
        classNames={{
          enter: "modal-enter",
          exit: "modal-exit",
        }}
        unmountOnExit
      >
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          style={{ outline: "none" }}
        >
          <Box sx={{ ...modalStyle, outline: "none" }}>
            <WarningAmberIcon sx={{ color: "#FFA000", fontSize: 60, mb: 2 }} />
            <Box
              sx={{
                fontWeight: "bold",
                fontSize: "clamp(18px, 1.7vw, 22px)",
                mb: 1,
              }}
            >
              Logout
            </Box>
            <Box
              sx={{
                color: "#555",
                mb: 2,
                fontSize: "clamp(12px, 1.2vw, 15px)",
                textWrap: "nowrap",
              }}
            >
              Are you sure you want to logout?
            </Box>
            <button
              className="btn btn-primary"
              onClick={handleLogout}
              style={{ fontSize: "clamp(13px, 1.3vw, 16px)" }}
            >
              Confirm
            </button>
          </Box>
        </Modal>
      </CSSTransition>
    </div >
  );
}